(ns hai.spaces.table
  "The spaces list page: a table with per-row and bulk delete, plus the
   New-space action."
  (:require
   [com.wsscode.pathom3.connect.operation :as pco]
   [hai.sharing.components :as sharing.components]
   [hai.spaces.data :as data]
   [hai.ui.components :as c]
   [hai.ui.components.card :as card]
   [hai.ui.components.collection.bulk-actions :as bulk-actions]
   [hai.ui.components.collection.filters :as filters]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.components.new-design.button :as button]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [>e]]
   [hai.web-app.layout :as layout]
   [hai.web-app.routing :as routing]
   [lambdaisland.deja-fu :as fu]
   [reagent.core :as r]))

(def ^:private trash-icon (icons/mui-icon :outlined :delete))

(defn- more-menu-render
  "Per-row ⋮ menu with Delete, shown only to the space-mgmt owner (`space/delete`
   is owner-gated). Empty cell otherwise."
  [{:space/keys [uuid title deletable-by-me?]}]
  (r/with-let [!delete-open? (r/atom false)]
    (if deletable-by-me?
      [:<>
       [c/AlertDialogRoot
        {:open @!delete-open? :onOpenChange #(reset! !delete-open? %)}
        [c/AlertDialogContent
         [c/AlertDialogTitle "Delete space"]
         [c/AlertDialogDescription
          "Are you sure you want to delete the space " [:strong title] "?"]
         [c/Flex {:justify "end" :gap "3" :mt "4"}
          [c/Button {:variant "soft" :color "gray"
                     :on-click #(reset! !delete-open? false)}
           "Cancel"]
          [c/Button {:variant "solid" :color "red"
                     :on-click (fn []
                                 (reset! !delete-open? false)
                                 (>e [::data/delete uuid]))}
           "Delete"]]]]
       [table/more-menu-cell
        [["Delete" (fn [_] (reset! !delete-open? true)) trash-icon]]]]
      [c/TableCell])))

(defn spaces-table
  []
  [collection/component
   {:id ::table
    :query [{:spaces/table
             [{:collection/items [:space/uuid
                                  (pco/? :space/title)
                                  (pco/? :space/description)
                                  :space/created-at
                                  :space/updated-at
                                  :user.entity/permissions
                                  ;; Delete is owner-gated (the space-mgmt owner),
                                  ;; not admin-gated — see `more-menu-render`.
                                  :space/deletable-by-me?
                                  sharing.components/shared-with-query]}]}]
    :paginate? true
    ;; Newest first by default.
    :pagination {:direction :desc :sort-attrs [:space/created-at]}
    :bulk-actions [{:title     "Delete"
                    ;; Owner-gated like the per-row menu: only rows whose
                    ;; space-mgmt the requester owns can be deleted.
                    :disabled? (complement :space/deletable-by-me?)
                    :mutation  'space/delete
                    :alert {:content
                            (fn [{:table/keys [selected] :as table*} action]
                              [bulk-actions/confirm-dialog
                               {:table table* :action action}
                               [bulk-actions/title "Delete spaces"]
                               [bulk-actions/description
                                "Are you sure you want to delete the following spaces?"]
                               (into [:ul]
                                     (map (fn [{:space/keys [title]}]
                                            [:li [:strong (or title "Untitled")]]))
                                     selected)])}}]
    :card (fn [collection item]
            [card/card
             [cards/card-select-checkbox collection item]
             [cards/column-render collection item]])
    :columns [{:header "Space"
               :attr   :space/title
               :sort?  true
               :filter (filters/text-search {:placeholder "Space name..."})
               :render (fn title-render
                         [{:space/keys [uuid title]}]
                         [table/table-cell
                          [c/Link
                           {:style   {:cursor "pointer"}
                            :href    (routing/href :spaces/detail {:space/uuid uuid})
                            :variant "ghost"}
                           title]])}
              {:header "Description"
               :attr   :space/description
               :sort?  true
               :filter (filters/text-search {:placeholder "Description..."})}
              {:header "Created"
               :attr   :space/created-at
               :sort?  true
               :render (fn created-render
                         [{:space/keys [created-at]}]
                         [table/table-cell
                          (fu/format created-at "dd MMM y")])}
              sharing.components/shared-with-column
              {:header "" :render more-menu-render}]}])

(defn page
  [_]
  [layout/page
   [layout/breadcrumbs]
   [layout/heading]
   [layout/lead*
    [layout/lead "Group notes and tasks into a space and share them with your team in one place."]
    [:div.buttons
     [button/dark-small
      {:on-click (fn [_] (>e [::data/create "New space"]))
       :text     "New space"
       :icon     [icons/plus]}]]]
   [layout/content
    [spaces-table]]])
